import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  TrendingUp, 
  FileText, 
  Building, 
  Users, 
  Shield, 
  Banknote,
  Calculator,
  BarChart3,
  CheckCircle,
  ArrowRight,
  Mail,
  X
} from "lucide-react";

const InvestmentAdvisor = () => {
  const navigate = useNavigate();

  const [showFormsModal, setShowFormsModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [forms, setForms] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== 'https://docgen-platform-1.preview.emergentagent.com' 
    ? process.env.REACT_APP_BACKEND_URL 
    : 'http://localhost:8001';
  const services = [
    {
      icon: FileText,
      title: "SAFE Agreements",
      description: "Simple Agreement for Future Equity documents for startup funding",
      timeEstimate: "25-45 minutes",
      typicalFees: "$3,000 - $8,000",
      features: [
        "Valuation Cap Setup",
        "Discount Rate Configuration", 
        "Pro Rata Rights",
        "Most Favored Nation Clauses"
      ]
    },
    {
      icon: TrendingUp,
      title: "Series A Terms",
      description: "Comprehensive Series A term sheets and investment documentation",
      timeEstimate: "45-90 minutes",
      typicalFees: "$8,000 - $25,000",
      features: [
        "Term Sheet Templates",
        "Liquidation Preferences",
        "Anti-dilution Provisions",
        "Board Composition"
      ]
    },
    {
      icon: Banknote,
      title: "Convertible Notes",
      description: "Debt instruments that convert to equity in future funding rounds",
      timeEstimate: "30-60 minutes", 
      typicalFees: "$2,500 - $7,500",
      features: [
        "Interest Rate Setup",
        "Maturity Date Configuration",
        "Conversion Mechanics",
        "Default Provisions"
      ]
    },
    {
      icon: Building,
      title: "M&A Transactions", 
      description: "Merger and acquisition documentation and due diligence support",
      timeEstimate: "60-120 minutes",
      typicalFees: "$15,000 - $50,000",
      features: [
        "Purchase Agreements",
        "Due Diligence Checklists",
        "Representations & Warranties",
        "Escrow Arrangements"
      ]
    }
  ];

  const handleGetStarted = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/investment/get-started`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service_type: 'safe', user_email: email })
      });
      
      if (response.ok) {
        const data = await response.json();
        navigate(`/questionnaire/${data.service.id}`);
      } else {
        alert('Error starting investment docs. Please try again.');
      }
    } catch (error) {
      console.error('Get started error:', error);
      alert('Error starting investment docs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewForms = async (serviceType) => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/investment/forms/${serviceType}`);
      
      if (response.ok) {
        const data = await response.json();
        setForms(data.forms);
        setSelectedService(data);
        setShowFormsModal(true);
      } else {
        alert('Error loading forms. Please try again.');
      }
    } catch (error) {
      console.error('View forms error:', error);
      alert('Error loading forms. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  const handleStartSaving = () => {
    navigate('/services');
  };

  const benefits = [
    {
      icon: Calculator,
      title: "Cost Calculator",
      description: "Estimate legal costs and compare with traditional attorney fees"
    },
    {
      icon: BarChart3,
      title: "Market Analytics",
      description: "Real-time funding data and market insights for better decisions"
    },
    {
      icon: Shield,
      title: "Compliance Verification",
      description: "Ensure all documents meet SEC and state regulatory requirements"
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description: "Share documents and collaborate with co-founders and investors"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-yellow-500 text-black hover:bg-yellow-600">
              Investment & Startup Legal
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Investment Advisory & Startup Legal
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Expert guidance for startup funding, SAFEs, convertible notes, Series A, acquisitions, 
              and all investment transactions. Powered by real market data and AI legal expertise.
            </p>
            <Button 
              size="lg"
              onClick={handleGetStarted}
              disabled={loading}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8 py-4"
            >
              {loading ? "Loading..." : "Get Started with Investment Docs"}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Investment Legal Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From seed funding to IPO, we provide comprehensive legal support for every stage of your investment journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow border-gray-200">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-3 bg-yellow-100 rounded-lg">
                      <service.icon className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-gray-800">
                        {service.title}
                      </CardTitle>
                      <p className="text-sm text-gray-500">
                        Estimated Time: {service.timeEstimate}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Typical Legal Fees:</span>
                      <span className="text-sm font-bold text-red-600">{service.typicalFees}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">FormForgePro Price:</span>
                      <span className="text-sm font-bold text-green-600">$49 per form to $99 per month</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-medium text-gray-800 mb-2">Included:</h4>
                    <ul className="space-y-1">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button 
                    onClick={() => handleViewForms(service.title.toLowerCase().includes('safe') ? 'safe' : 
                                                 service.title.toLowerCase().includes('series') ? 'series_a' :
                                                 service.title.toLowerCase().includes('convertible') ? 'convertible' : 'ma')}
                    disabled={loading}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                  >
                    {loading ? "Loading..." : "View Forms"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Advanced Investment Tools
            </h2>
            <p className="text-xl text-gray-600">
              More than just documents - get comprehensive investment insights and tools
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="p-3 bg-yellow-100 rounded-lg w-fit mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-yellow-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Savings Calculator */}
      <section className="bg-gray-800 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              See Your Investment Legal Savings
            </h2>
            <p className="text-xl text-gray-300">
              Compare traditional legal fees with FormForgePro pricing
            </p>
          </div>

          <div className="bg-white rounded-lg overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">Service</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">Traditional Legal Fees</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">FormForgePro Price</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-800">Your Savings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-800">SAFE Agreement</td>
                    <td className="px-6 py-4 text-sm text-red-600 font-medium">$3,000-$8,000</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">$49 per form to $99 per month</td>
                    <td className="px-6 py-4 text-sm text-green-700 font-bold">96-99%</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-800">Series A Terms</td>
                    <td className="px-6 py-4 text-sm text-red-600 font-medium">$8,000-$25,000</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">$49 per form to $99 per month</td>
                    <td className="px-6 py-4 text-sm text-green-700 font-bold">97-99%</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-800">Convertible Notes</td>
                    <td className="px-6 py-4 text-sm text-red-600 font-medium">$2,500-$7,500</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">$49 per form to $99 per month</td>
                    <td className="px-6 py-4 text-sm text-green-700 font-bold">96-99%</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-800">M&A Transaction</td>
                    <td className="px-6 py-4 text-sm text-red-600 font-medium">$15,000-$50,000</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">$49 per form to $99 per month</td>
                    <td className="px-6 py-4 text-sm text-green-700 font-bold">98-99%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-center mt-8">
            <Button 
              size="lg"
              onClick={handleStartSaving}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-8 py-3 mr-4"
            >
              Start Saving Now
            </Button>

          </div>
        </div>
      </section>



      {/* Forms Modal */}
      {showFormsModal && selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedService.description}
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowFormsModal(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="font-semibold text-gray-800">{selectedService.total_forms}</div>
                    <div className="text-sm text-gray-600">Total Forms</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="font-semibold text-gray-800">{selectedService.estimated_time}</div>
                    <div className="text-sm text-gray-600">Est. Time</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="font-semibold text-green-600">{selectedService.legal_fees_saved}</div>
                    <div className="text-sm text-gray-600">Legal Fees Saved</div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                {forms.map((form, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-800">{form.name}</h4>
                      <Badge 
                        variant={form.complexity === 'Simple' ? 'default' : 
                                form.complexity === 'Moderate' ? 'secondary' : 'destructive'}
                      >
                        {form.complexity}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      {form.pages} pages • Ready to customize for your needs
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex space-x-3 pt-6 border-t">
                <Button
                  onClick={handleGetStarted}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  Get Started with These Forms
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowFormsModal(false)}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentAdvisor;